import React, { useState } from 'react'
import { useSWRConfig } from 'swr'
import { useTranslation } from '../../../../lib/i18n'
import Button from '../../../common/Button'
import JoinConfirmModal from './JoinConfirmModal'
import WithdrawApplicationModal from './WithdrawApplicationModal'
import useGroup from '../../../../lib/useGroup'
import { useCacheKey } from '../../../context/CacheKey'
import useUser from '../../../../lib/useUser'
import { useAnalytics } from '../../../context/AnalyticsProvider'

const JoinButton = ({ groupSlug, invite, onJoin }) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { group, acceptGroupMembership, createGroupMembership } = useGroup(
    groupSlug,
    { invite }
  )
  const { mutate } = useSWRConfig()
  const cacheKey = useCacheKey()
  const { track } = useAnalytics()
  const membershipStatus = group?.status?.toLowerCase()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const buttonLabel = () => {
    if (membershipStatus === 'applied') {
      return t('groups.pending')
    }
    if (membershipStatus === 'invited') {
      return t('groups.acceptInvite')
    }
    if (membershipStatus === 'active') {
      return t('groups.joined')
    }
    return t('groups.joinGroup')
  }

  const handleJoinGroup = async () => {
    if (loading) return // Prevent double click
    setLoading(true)
    let resp = null

    if (membershipStatus === 'applied') {
      track('group_withdraw_application_initiated', {
        group_slug: groupSlug,
        has_invite: !!invite,
      })
      setShowWithdrawModal(true)
      setLoading(false)
      return
    }
    if (membershipStatus === 'invited') {
      try {
        resp = await acceptGroupMembership(user.id)
        track('group_invite_accepted', {
          group_slug: groupSlug,
          group_name: group?.name,
          has_invite: !!invite,
        })
        if (onJoin) {
          onJoin()
        }
      } catch (error) {
        track('group_invite_accept_failed', {
          group_slug: groupSlug,
          error: error.message,
        })
        console.error(error)
      }
    } else if (group?.apply_to_join) {
      track('group_application_initiated', {
        group_slug: groupSlug,
        group_name: group?.name,
        has_invite: !!invite,
      })
      setShowConfirmModal(true)
      setLoading(false)
      return
    } else {
      try {
        resp = await createGroupMembership(user?.id)
        track('group_joined', {
          group_slug: groupSlug,
          group_name: group?.name,
          has_invite: !!invite,
        })
        if (onJoin) {
          onJoin()
        }
      } catch (error) {
        track('group_join_failed', {
          group_slug: groupSlug,
          error: error.message,
        })
        console.error(error)
      }
    }

    try {
      mutate(
        invite
          ? `/api/group_invite/${invite}`
          : `/api/groups/${groupSlug}.json`,
        (oldData) => ({
          group: { ...oldData?.group, ...resp?.group },
        }),
        false
      )
      mutate(cacheKey.value)
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  if (membershipStatus?.toLowerCase() === 'blocked') {
    return null
  }

  return (
    <>
      <Button
        tiny
        primary
        onClick={handleJoinGroup}
        disabled={loading || membershipStatus === 'active'}
      >
        {loading ? t('groups.joining') : buttonLabel()}
      </Button>
      <JoinConfirmModal
        groupSlug={groupSlug}
        isOpen={showConfirmModal}
        onRequestClose={() => setShowConfirmModal(false)}
        invite={invite}
        onJoin={onJoin}
      />
      <WithdrawApplicationModal
        groupSlug={groupSlug}
        isOpen={showWithdrawModal}
        onRequestClose={() => setShowWithdrawModal(false)}
        invite={invite}
      />
    </>
  )
}

export default JoinButton
